import { applyDesign } from './design.js';
import designManifestData from './templates/designs.json' with { type: 'json' };
import { sanitizeUrl } from './sanitize.js';
import { themeToCss } from './theme.js';
import { themeFontsHref } from './theme-presets.js';
import { DESIGN_EXTRA_TYPES } from './blocks.js';
import { extrasPalette } from './design-extras.js';
// Keep this in sync with packages/shared/src/site.css.
const BASE_CSS = `/* Sarnia Builder published-site base CSS */
*,*::before,*::after{box-sizing:border-box}
html{scroll-behavior:smooth}
body{margin:0;font-family:var(--site-body-font);background:var(--site-background);color:var(--site-text);line-height:1.6}
img{max-width:100%;height:auto;display:block}
a{color:var(--site-accent);text-decoration-color:var(--site-accent)}
a:hover{text-decoration-color:currentColor}
:focus-visible{outline:3px solid var(--site-accent);outline-offset:2px}
button:focus-visible,input:focus-visible,textarea:focus-visible,select:focus-visible{outline:3px solid var(--site-accent);outline-offset:2px}
.skip-link{position:absolute;left:-9999px;top:0;background:var(--site-accent);color:var(--site-accentText);padding:.75rem 1rem;z-index:200;border-radius:0 var(--site-radius-sm) 0 0}
.skip-link:focus{left:0}
.site-blocks{min-height:60vh}
.block{padding:var(--site-section-space) 1rem}
.block-inner{max-width:var(--site-container);margin:0 auto}
.block-heading{font-family:var(--site-heading-font);color:var(--site-heading);font-size:clamp(2rem,4.5vw,3.5rem);line-height:1.1;margin:0 0 1rem}
.block-copy{margin:0 0 1.5rem;color:var(--site-muted);max-width:45rem}
.block--header{position:sticky;top:0;z-index:100;background:var(--site-background);border-bottom:1px solid var(--site-border);padding:0 1rem}
.header-inner{display:flex;align-items:center;justify-content:space-between;gap:1rem;min-height:4.5rem;max-width:var(--site-container);margin:0 auto}
.brand{font-family:var(--site-heading-font);font-weight:700;font-size:1.35rem;color:var(--site-heading);text-decoration:none;white-space:nowrap}
.site-nav-list{display:flex;flex-wrap:wrap;gap:1.25rem;list-style:none;margin:0;padding:0}
.site-nav-list a{color:var(--site-text);text-decoration:none;font-weight:500}
.site-nav-list a:hover{color:var(--site-accent)}
.nav-toggle{display:none;background:var(--site-surface);border:1px solid var(--site-border);color:var(--site-heading);border-radius:var(--site-radius-sm);padding:.55rem .9rem;font-weight:700;cursor:pointer}
.header-cta{margin-left:.5rem;white-space:nowrap}
.site-nav-list.is-open .nav-toggle{display:block}
.button-row{display:flex;flex-wrap:wrap;gap:.75rem;margin-top:1.5rem}
.button{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;border-radius:var(--site-radius-sm);padding:.8rem 1.25rem;font-weight:700;text-decoration:none;border:1px solid transparent;cursor:pointer;font-family:inherit;font-size:1rem}
.button--primary{background:var(--site-accent);color:var(--site-accentText)}
.button--primary:hover{filter:brightness(.94)}
.button--secondary{background:transparent;color:color-mix(in srgb,var(--site-accent) 84%,#000);border-color:var(--site-accent)}
.button--link{background:transparent;border:none;color:var(--site-accent);text-decoration:underline;cursor:pointer;font-weight:700;padding:0}
.block--hero{padding-top:2rem;padding-bottom:calc(var(--site-section-space) / 2)}
.hero-inner{display:grid;grid-template-columns:1.1fr .9fr;gap:3rem;align-items:center}
.eyebrow{font-family:var(--site-mono-font);color:var(--site-muted);text-transform:uppercase;letter-spacing:.08em;margin:0 0 .6rem}
.hero-title{font-family:var(--site-heading-font);color:var(--site-heading);font-size:clamp(2.4rem,6vw,4.8rem);line-height:1.05;margin:0}
.hero-lead{font-size:1.25rem;color:var(--site-muted);margin:1.5rem 0 0;max-width:38rem}
.hero-image{border-radius:var(--site-radius-lg);overflow:hidden;background:var(--site-surface)}
.block--text .block-inner>*{max-width:45rem}
.block--text.align-center .block-inner{text-align:center}
.block--text.align-center .block-inner>*{margin-left:auto;margin-right:auto}
.block--text.align-right .block-inner{text-align:right}
.image-placeholder,.site-image{background:var(--site-surface);border:1px dashed var(--site-border);border-radius:var(--site-radius-md);min-height:12rem;display:flex;align-items:center;justify-content:center;color:var(--site-muted);font-family:var(--site-mono-font)}
.image-placeholder::after{content:"Image not set";font-size:.9rem}
/* An image the owner has not chosen yet stays out of the published page; the editor preview turns it back on. */
.image-placeholder{display:none}
/* Section style: a background and spacing for any block. The accent variant remaps the colour variables so every card, button and heading inside stays readable. */
.block-bg-surface{background:var(--site-surface);--site-surface:var(--site-background)}
.block-bg-tint{background:var(--site-tint,color-mix(in srgb,var(--site-accent-base) 8%,var(--site-background)))}
.block-bg-accent{background:var(--site-accent-base);--site-background:var(--site-accent-base);--site-text:var(--site-accentText-base);--site-heading:var(--site-accentText-base);--site-muted:var(--site-on-accent-muted,color-mix(in srgb,var(--site-accentText-base) 84%,var(--site-accent-base)));--site-surface:var(--site-on-accent-surface,color-mix(in srgb,var(--site-accent-base) 80%,#000));--site-border:var(--site-on-accent-border,color-mix(in srgb,var(--site-accentText-base) 32%,var(--site-accent-base)));--site-accent:var(--site-accentText-base);--site-accentText:var(--site-accent-base);color:var(--site-text)}
.block--stats .stats-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(10rem,1fr));gap:2rem;margin-top:1.5rem;text-align:center}
.stat-value{margin:0;font-family:var(--site-heading-font);font-size:clamp(2.2rem,5vw,3.6rem);line-height:1.05;color:var(--site-heading)}
.stat-label{margin:.35rem 0 0;color:var(--site-muted)}
.block--steps .steps-list{list-style:none;counter-reset:step;display:grid;grid-template-columns:repeat(auto-fit,minmax(14rem,1fr));gap:1.5rem;padding:0;margin:1.5rem 0 0}
.step{counter-increment:step;position:relative;padding:1.25rem;background:var(--site-surface);border:1px solid var(--site-border);border-radius:var(--site-radius-md)}
.step::before{content:counter(step);display:inline-flex;align-items:center;justify-content:center;width:2.25rem;height:2.25rem;margin-bottom:.75rem;border-radius:999px;background:var(--site-accent);color:var(--site-accentText);font-weight:700}
.step h3{margin:0 0 .35rem;font-family:var(--site-heading-font);color:var(--site-heading);font-size:1.15rem}
.step p{margin:0;color:var(--site-text)}
.block--quote .quote-figure{max-width:46rem;margin:0 auto;text-align:center}
.quote-text{margin:0;font-family:var(--site-heading-font);color:var(--site-heading);font-size:clamp(1.5rem,3.4vw,2.4rem);line-height:1.25}
.block--quote figcaption{margin-top:1rem;color:var(--site-muted)}
.block--banner{background:var(--site-accent-base);color:var(--site-accentText-base);padding:.9rem 1rem}
.block--banner .banner-inner{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.75rem 1.25rem;text-align:center}
.banner-text{margin:0;font-weight:600}
.block--banner .button{background:var(--site-accentText-base);color:var(--site-accent-base);border-color:var(--site-accentText-base);padding:.45rem 1rem}
.block-bg-accent .button--secondary{color:var(--site-accent);border-color:var(--site-accent)}
.block-space-compact{--site-section-space:2.25rem}
.block-space-roomy{--site-section-space:7rem}
.block--image-text .block-inner:has(.image-placeholder),.hero-inner:has(.image-placeholder){grid-template-columns:1fr}
.site-image{overflow:hidden;border-style:solid;border-color:var(--site-border);border-width:1px}
.site-image img{width:100%;height:100%;object-fit:cover}
.block--image-text .block-inner{display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center}
.block--image-text.image-position-right .block-inner{grid-template-columns:1fr 1fr}
.block--image-text.image-position-right .image-text-image{order:2}
.block--feature-grid .items-grid{display:grid;grid-template-columns:repeat(var(--grid-columns,3),1fr);gap:1.5rem}
.block--feature-grid[data-columns="2"]{--grid-columns:2}
.block--feature-grid[data-columns="3"]{--grid-columns:3}
.block--feature-grid[data-columns="4"]{--grid-columns:4}
.feature-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:var(--site-radius-md);padding:1.5rem}
.feature-icon{width:2rem;height:2rem;color:var(--site-accent);margin-bottom:1rem}
.block--gallery .gallery-grid{display:grid;grid-template-columns:repeat(var(--grid-columns,3),1fr);gap:1rem}
.block--gallery[data-columns="2"]{--grid-columns:2}
.block--gallery[data-columns="3"]{--grid-columns:3}
.block--gallery[data-columns="4"]{--grid-columns:4}
.gallery-link{display:block;text-decoration:none}
.block--pricing .pricing-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(15rem,1fr));gap:1.25rem}
.pricing-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:var(--site-radius-md);padding:1.5rem}
.pricing-card.highlighted{border-color:var(--site-accent);background:var(--site-accent);color:var(--site-accentText)}
.pricing-card.highlighted .pricing-name,.pricing-card.highlighted .pricing-price,.pricing-card.highlighted .pricing-unit{color:var(--site-accentText)}
.pricing-name{font-family:var(--site-heading-font);font-size:1.25rem;color:var(--site-heading);margin:0 0 .5rem}
.pricing-price{font-family:var(--site-heading-font);font-size:2.5rem;color:var(--site-heading);margin:0}
.pricing-unit{color:var(--site-muted)}
.pricing-features{list-style:none;margin:1.25rem 0 0;padding:0;display:grid;gap:.5rem}
.pricing-features li{padding-left:1.25rem;position:relative}
.pricing-features li::before{content:"✓";position:absolute;left:0;color:var(--site-accent)}
.pricing-card.highlighted .pricing-features li::before{color:var(--site-accentText)}
.block--testimonials .testimonials-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(15rem,1fr));gap:1.25rem}
.testimonial-card{background:var(--site-surface);border:1px solid var(--site-border);border-radius:var(--site-radius-md);padding:1.5rem}
.testimonial-quote{margin:0;font-size:1.1rem}
.testimonial-name{font-weight:700;margin-top:1rem}
.testimonial-role{color:var(--site-muted);font-size:.9rem}
.block--menu .menu-section{margin-bottom:2rem}
.menu-section-heading{font-family:var(--site-heading-font);color:var(--site-heading);font-size:1.35rem;margin:0 0 1rem}
.menu-item{display:grid;grid-template-columns:1fr auto;gap:.5rem 1rem;padding:.85rem 0;border-bottom:1px solid var(--site-border)}
.menu-item-name{font-weight:700}
.menu-item-description{color:var(--site-muted);grid-column:1 / -1;order:3;margin-top:-.45rem}
.menu-item-price{font-weight:700;grid-column:2;grid-row:1}
.block--hours .hours-list{display:grid;gap:.6rem;max-width:36rem}
.hours-row{display:flex;justify-content:space-between;border-bottom:1px solid var(--site-border);padding-bottom:.5rem}
.hours-days{font-weight:700}
.hours-time{color:var(--site-muted)}
.block--contact .block-inner>*{max-width:40rem}
.contact-form{display:grid;gap:1rem}
.form-field{display:grid;gap:.35rem}
.form-field label{font-weight:700}
.form-field input,.form-field textarea{font:inherit;background:var(--site-surface);border:1px solid var(--site-border);color:var(--site-text);padding:.8rem .9rem;border-radius:var(--site-radius-sm);width:100%}
.form-field textarea{min-height:8rem;resize:vertical}
.hp-field{position:absolute;left:-9999px;height:1px;width:1px;overflow:hidden}
.block--map .map-address{margin:0 0 1.25rem;font-style:normal;max-width:36rem}
.block--faq .faq-item{border-bottom:1px solid var(--site-border);padding:1rem 0}
.block--faq summary{font-weight:700;cursor:pointer;font-family:var(--site-heading-font)}
.block--faq .faq-answer{margin-top:.75rem;color:var(--site-muted)}
.block--cta{border-radius:var(--site-radius-lg);margin:var(--site-section-space) 1rem;padding:var(--site-section-space) 1.25rem;color:var(--site-accentText)}
.block--cta.bg-accent{background:var(--site-accent);color:var(--site-accentText)}
.block--cta.bg-accent .cta-title,.block--cta.bg-accent .cta-body{color:var(--site-accentText)}
.block--cta.bg-ink{background:var(--site-heading);color:var(--site-background)}
.block--cta.bg-ink .cta-title,.block--cta.bg-ink .cta-body{color:var(--site-background)}
.block--cta.bg-paper{background:var(--site-background);color:var(--site-text);border:1px solid var(--site-border)}
.block--cta .cta-title{color:var(--site-heading);font-family:var(--site-heading-font);font-size:clamp(2rem,4vw,3rem);margin:0}
.block--cta .cta-body{margin:1rem 0 0}
.block--cta .button--primary{background:var(--site-accent);color:var(--site-accentText)}
.block--cta.bg-accent .button--primary{background:var(--site-accentText);color:var(--site-accent)}
.block--cta.bg-ink .button--primary{background:var(--site-background);color:var(--site-heading)}
.block--cta.bg-paper .button--primary{background:var(--site-accent);color:var(--site-accentText)}
.block--social .social-list{display:flex;flex-wrap:wrap;gap:1rem;list-style:none;padding:0;margin:0}
.social-list a{text-decoration:none;font-weight:700}
.block--footer{background:var(--site-text);color:var(--site-background);padding:3rem 1rem}
.block--footer .footer-inner{max-width:var(--site-container);margin:0 auto;display:grid;grid-template-columns:1fr auto;gap:1rem;align-items:start}
.footer-brand{font-family:var(--site-heading-font);font-size:1.25rem}
.footer-tagline{color:color-mix(in srgb,var(--site-background) 78%,transparent);margin-top:.25rem}
.footer-links{display:flex;flex-wrap:wrap;gap:1rem;list-style:none;padding:0;margin:0}
.footer-links a{color:var(--site-background)}
.footer-copyright{grid-column:1 / -1;color:color-mix(in srgb,var(--site-background) 72%,transparent);font-size:.9rem;margin-top:1.5rem}
.site-report-abuse{max-width:var(--site-container);margin:0 auto;padding:0 1rem 2rem;text-align:right}
.report-abuse-link{background:none;border:none;color:var(--site-muted);text-decoration:underline;cursor:pointer;font:inherit}
.report-abuse-link:disabled{opacity:.6;cursor:default}
.spacer-line{position:absolute;top:50%;height:1px;width:100%;background:var(--site-border)}
.block--spacer{position:relative;overflow:hidden;padding:0}
.block--spacer .spacer-line{display:none}
.block--spacer[data-divider="true"] .spacer-line{display:block}
.site-lightbox{border:0;padding:0;max-width:90vw;max-height:90vh;background:var(--site-background);border-radius:var(--site-radius-lg);box-shadow:0 2rem 6rem rgba(0,0,0,.24)}
.site-lightbox::backdrop{background:rgba(0,0,0,.72)}
.site-lightbox-image{max-width:100%;max-height:80vh;object-fit:contain;display:block}
.site-lightbox-close{position:absolute;top:.5rem;right:.5rem;background:var(--site-accent);color:var(--site-accentText);border:0;border-radius:var(--site-radius-sm);padding:.45rem .8rem;font-weight:700;cursor:pointer}
@media (max-width:900px){.hero-inner,.block--image-text .block-inner{grid-template-columns:1fr}.hero-image,.image-text-image{order:-1}}
@media (max-width:760px){.header-inner{flex-wrap:wrap;padding:.75rem 0}.nav-toggle{display:inline-flex}.site-nav{display:none;width:100%;order:10}.site-nav.is-open{display:block}.site-nav-list{flex-direction:column;gap:.75rem;padding:.5rem 0}.header-cta{display:none}.block--feature-grid .items-grid,.block--gallery .gallery-grid,.block--pricing .pricing-grid{grid-template-columns:1fr}.block--footer .footer-inner{grid-template-columns:1fr;grid-template-rows:auto auto}.footer-links{flex-direction:column;gap:.5rem}}
@media (prefers-reduced-motion:reduce){html{scroll-behavior:auto}*{animation-duration:.01ms !important;animation-iteration-count:1 !important;transition-duration:.01ms !important}}
`;
const SITE_JS = `(function () {
  'use strict';

  function qa(selector) {
    return Array.prototype.slice.call(document.querySelectorAll(selector));
  }

  function initNavToggles() {
    qa('.nav-toggle').forEach(function (button) {
      var nav = document.getElementById(button.getAttribute('aria-controls') || '');
      if (!nav) return;
      button.addEventListener('click', function () {
        var open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        button.textContent = open ? 'Menu' : 'Close';
        nav.classList.toggle('is-open', !open);
      });
    });
  }

  function initGalleryLightbox() {
    qa('.gallery-link').forEach(function (link) {
      link.addEventListener('click', function (event) {
        var img = link.querySelector('img');
        if (!img) return;
        event.preventDefault();
        var dialog = document.createElement('dialog');
        dialog.className = 'site-lightbox';
        var close = document.createElement('button');
        close.type = 'button';
        close.className = 'site-lightbox-close';
        close.textContent = 'Close';
        var clone = img.cloneNode(true);
        clone.removeAttribute('loading');
        clone.className = 'site-lightbox-image';
        var imgWrap = document.createElement('div');
        imgWrap.appendChild(close);
        imgWrap.appendChild(clone);
        dialog.appendChild(imgWrap);
        document.body.appendChild(dialog);
        dialog.showModal();
        close.addEventListener('click', function () {
          dialog.close();
          dialog.remove();
        });
        dialog.addEventListener('click', function (e) {
          if (e.target === dialog) {
            dialog.close();
            dialog.remove();
          }
        });
      });
    });
  }

  function initReportAbuseLink() {
    qa('.report-abuse-link').forEach(function (button) {
      button.addEventListener('click', function () {
        var path = button.getAttribute('data-abuse-path') || '';
        if (!path) return;
        var reason = window.prompt('Please tell us why you are reporting this site.');
        if (!reason || !reason.trim()) return;
        fetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: reason.trim(), details: '', reporterEmail: '' })
        })
          .then(function (response) {
            if (response.ok) {
              button.textContent = 'Report received';
              button.disabled = true;
            } else {
              window.alert('Could not send your report. Please try again.');
            }
          })
          .catch(function () {
            window.alert('Could not send your report. Please try again.');
          });
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initNavToggles();
    initGalleryLightbox();
    initReportAbuseLink();
  });
})();`;
const CSP = "default-src 'none'; script-src 'self'; style-src 'self'; img-src 'self'; font-src 'self'; connect-src 'self'; form-action 'self'; frame-ancestors 'none'; base-uri 'none'";
// Same policy, plus the one font host (stylesheet + font files) when the theme uses web fonts.
const CSP_WITH_FONTS = CSP.replace("style-src 'self'", "style-src 'self' https://fonts.googleapis.com").replace("font-src 'self'", "font-src 'self' https://fonts.gstatic.com");
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
function escapeAttribute(value) {
    return escapeHtml(value);
}
function safeDomId(id) {
    const cleaned = id.replace(/[^A-Za-z0-9_-]/g, '-');
    return cleaned || 'block';
}
function safeHref(value) {
    const clean = sanitizeUrl(value, { allowRelative: true });
    return clean || '#';
}
function slugFromSiteUrl(siteUrl) {
    if (!siteUrl)
        return null;
    try {
        const url = new URL(siteUrl);
        const label = url.hostname.split('.')[0];
        return label || null;
    }
    catch {
        return null;
    }
}
function renderIcon(icon) {
    const icons = {
        anchor: '<path d="M12 3l2 3h-1v3h2.5l3.5 5h2l-2.2-8H21a3 3 0 0 0 2.2-1l-3.2-1L19 3h-2v2h-2V3h-1v3h-1V5H9v2H8V5H6l.8 3L3 9.5a3 3 0 0 0 2.2 1h2L5.8 15H9l1-3.5 1 3.5h4.2l-.8-3h2V22h2v-9H20z"/>',
        food: '<path d="M5 3v7a4 4 0 0 0 3 3.9V21h2v-7.1A4 4 0 0 0 13 10V3h-2v6a1 1 0 0 1-2 0V3h-2v6a1 1 0 0 1-2 0V3H5z"/>',
        spark: '<path d="M12 2l1.9 5.1L19 9l-5.1 1.9L12 16l-1.9-5.1L5 9l5.1-1.9L12 2zm7 12l1 2.7L22.7 18l-2.7 1L19 21.7l-1-2.7L15.3 18l2.7-1L19 14z"/>',
        leaf: '<path d="M5 20c8 0 14-4 14-14 0 0-6 1-10 4L5 14v6z"/>',
        wrench: '<path d="M14.7 6.3a4 4 0 0 0-5.4 4.5L3 16.5V21h4.5l6.3-6.3a4 4 0 0 0 4.5-5.4l-2.6 2.6-2.4-.6-.6-2.4 2.6-2.6z"/>',
        heart: '<path d="M12 21s-7.5-4.8-9.7-9C.9 8.6 3.1 5 6.5 5c2 0 3.4 1 4.5 2.5C12.1 6 13.5 5 15.5 5c3.4 0 5.6 3.6 4.2 7-2.2 4.2-7.7 9-7.7 9z"/>',
        star: '<path d="M12 2l2.9 6.2 6.8.9-5 4.8 1.3 6.8-6-3.3-6 3.3 1.3-6.8-5-4.8 6.8-.9L12 2z"/>'
    };
    if (!icons[icon]) {
        return '<span class="icon icon--none" aria-hidden="true"></span>';
    }
    return `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">${icons[icon]}</svg>`;
}
function renderInlineRichText(nodes) {
    let out = '';
    for (const node of nodes) {
        if (node.kind === 'br') {
            out += '<br>';
            continue;
        }
        let text = escapeHtml(node.text);
        if (node.bold)
            text = `<strong>${text}</strong>`;
        if (node.italic)
            text = `<em>${text}</em>`;
        if (node.link) {
            const href = escapeAttribute(safeHref(node.link));
            const rel = /^https?:\/\//i.test(safeHref(node.link)) ? ' rel="noopener"' : '';
            text = `<a href="${href}"${rel}>${text}</a>`;
        }
        out += text;
    }
    return out;
}
function renderRichText(richText) {
    let out = '<div class="rich-text">';
    for (const node of richText) {
        if (node.kind === 'p') {
            out += `<p>${renderInlineRichText(node.children)}</p>`;
        }
        else if (node.kind === 'ul' || node.kind === 'ol') {
            const tag = node.kind;
            out += `<${tag}>`;
            for (const item of node.items) {
                out += `<li>${renderInlineRichText(item)}</li>`;
            }
            out += `</${tag}>`;
        }
    }
    out += '</div>';
    return out;
}
function renderImage(image, assets, loading = 'lazy') {
    const asset = image.assetId ? assets[image.assetId] : undefined;
    if (!asset) {
        const label = image.alt || 'Image not set';
        return `<div class="image-placeholder" role="img" aria-label="${escapeAttribute(label)}"></div>`;
    }
    const alt = escapeAttribute(asset.alt || image.alt || '');
    const width = asset.width ? ` width="${asset.width}"` : '';
    const height = asset.height ? ` height="${asset.height}"` : '';
    return `<img class="site-image" src="${escapeAttribute(asset.url)}" alt="${alt}"${width}${height} loading="${loading}" decoding="async">`;
}
function renderButtonGroup(primary, secondary) {
    if (!primary && !secondary)
        return '';
    let out = '<div class="button-row">';
    if (primary)
        out += `<a class="button button--primary" href="${escapeAttribute(safeHref(primary.href))}">${escapeHtml(primary.label)}</a>`;
    if (secondary)
        out += `<a class="button button--secondary" href="${escapeAttribute(safeHref(secondary.href))}">${escapeHtml(secondary.label)}</a>`;
    out += '</div>';
    return out;
}
function renderLinkList(links, className) {
    let out = `<ul class="${className}">`;
    for (const link of links) {
        out += `<li><a href="${escapeAttribute(safeHref(link.href))}">${escapeHtml(link.label)}</a></li>`;
    }
    out += '</ul>';
    return out;
}
function renderHeader(props, blockId) {
    const navId = `nav-${safeDomId(blockId)}`;
    return `<header class="block block--header" data-block-id="${escapeAttribute(`block-${safeDomId(blockId)}`)}">
  <div class="header-inner">
    <a class="brand" href="#top">${escapeHtml(props.brand)}</a>
    <button type="button" class="nav-toggle" aria-expanded="false" aria-controls="${escapeAttribute(navId)}">Menu</button>
    <nav id="${escapeAttribute(navId)}" class="site-nav" aria-label="Main navigation">${renderLinkList(props.nav, 'site-nav-list')}</nav>
    ${props.cta ? `<a class="button button--primary header-cta" href="${escapeAttribute(safeHref(props.cta.href))}">${escapeHtml(props.cta.label)}</a>` : ''}
  </div>
</header>`;
}
function renderHero(props, assets) {
    return `<section class="block block--hero">
  <div class="block-inner hero-inner">
    <div class="hero-copy">
      <p class="eyebrow">${escapeHtml(props.eyebrow)}</p>
      <h1 class="hero-title">${escapeHtml(props.title)}</h1>
      <p class="hero-lead">${escapeHtml(props.lead)}</p>
      ${renderButtonGroup(props.primaryCta, props.secondaryCta)}
    </div>
    <div class="hero-image">${renderImage(props.image, assets)}</div>
  </div>
</section>`;
}
function renderText(props) {
    return `<section class="block block--text align-${escapeAttribute(props.align)}">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    ${renderRichText(props.body)}
  </div>
</section>`;
}
function renderImageText(props, assets) {
    const orderClass = props.imagePosition === 'right' ? 'image-position-right' : '';
    return `<section class="block block--image-text ${orderClass}">
  <div class="block-inner">
    <div class="image-text-image">${renderImage(props.image, assets)}</div>
    <div class="image-text-copy">
      <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
      ${renderRichText(props.body)}
      ${props.cta ? `<div class="button-row"><a class="button button--primary" href="${escapeAttribute(safeHref(props.cta.href))}">${escapeHtml(props.cta.label)}</a></div>` : ''}
    </div>
  </div>
</section>`;
}
function renderFeatureGrid(props) {
    return `<section class="block block--feature-grid" data-columns="${escapeAttribute(String(props.columns))}">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <p class="block-copy">${renderRichText(props.body)}</p>
    <div class="items-grid">
      ${props.items.map((item) => `<article class="feature-card"><div class="feature-icon">${renderIcon(item.icon)}</div><h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.text)}</p></article>`).join('')}
    </div>
  </div>
</section>`;
}
function renderStats(props) {
    return `<section class="block block--stats">
  <div class="block-inner">
    ${props.heading ? `<h2 class="block-heading">${escapeHtml(props.heading)}</h2>` : ''}
    <div class="stats-grid">
      ${props.items.map((item) => `<div class="stat"><p class="stat-value">${escapeHtml(item.value)}</p><p class="stat-label">${escapeHtml(item.label)}</p></div>`).join('')}
    </div>
  </div>
</section>`;
}
function renderSteps(props) {
    const intro = props.body.length > 0 ? `<p class="block-copy">${renderRichText(props.body)}</p>` : '';
    return `<section class="block block--steps">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    ${intro}
    <ol class="steps-list">
      ${props.items.map((item) => `<li class="step"><h3>${escapeHtml(item.title)}</h3>${item.text ? `<p>${escapeHtml(item.text)}</p>` : ''}</li>`).join('')}
    </ol>
  </div>
</section>`;
}
function renderQuote(props) {
    const who = [props.author, props.role].filter(Boolean).map((part) => escapeHtml(part)).join(', ');
    return `<section class="block block--quote">
  <div class="block-inner">
    <figure class="quote-figure">
      <blockquote class="quote-text">${escapeHtml(props.text)}</blockquote>
      ${who ? `<figcaption>${who}</figcaption>` : ''}
    </figure>
  </div>
</section>`;
}
function renderBanner(props) {
    const cta = props.cta ? `<a class="button" href="${escapeAttribute(safeHref(props.cta.href))}">${escapeHtml(props.cta.label)}</a>` : '';
    return `<section class="block block--banner" aria-label="Announcement">
  <div class="block-inner banner-inner">
    <p class="banner-text">${escapeHtml(props.text)}</p>
    ${cta}
  </div>
</section>`;
}
function renderGallery(props, assets) {
    return `<section class="block block--gallery" data-columns="${escapeAttribute(String(props.columns))}">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <div class="gallery-grid">
      ${props.images.map((image) => {
        const asset = image.assetId ? assets[image.assetId] : undefined;
        if (!asset)
            return `<div class="gallery-box">${renderImage(image, assets)}</div>`;
        return `<a class="gallery-link" href="${escapeAttribute(asset.url)}">${renderImage(image, assets)}</a>`;
    }).join('')}
    </div>
  </div>
</section>`;
}
function renderPricing(props) {
    return `<section class="block block--pricing">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <p class="block-copy">${escapeHtml(props.description)}</p>
    <div class="pricing-grid">
      ${props.items.map((item) => `<div class="pricing-card${item.highlighted ? ' highlighted' : ''}">
        <h3 class="pricing-name">${escapeHtml(item.name)}</h3>
        <p class="pricing-price">${escapeHtml(item.price)} <span class="pricing-unit">${escapeHtml(item.unit)}</span></p>
        <ul class="pricing-features">${item.features.map((feature) => `<li>${escapeHtml(feature)}</li>`).join('')}</ul>
        ${item.cta ? `<div class="button-row"><a class="button button--primary" href="${escapeAttribute(safeHref(item.cta.href))}">${escapeHtml(item.cta.label)}</a></div>` : ''}
      </div>`).join('')}
    </div>
  </div>
</section>`;
}
function renderTestimonials(props) {
    return `<section class="block block--testimonials">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <div class="testimonials-grid">
      ${props.items.map((item) => `<figure class="testimonial-card"><blockquote class="testimonial-quote">“${escapeHtml(item.quote)}”</blockquote><figcaption><div class="testimonial-name">${escapeHtml(item.name)}</div><div class="testimonial-role">${escapeHtml(item.role)}</div></figcaption></figure>`).join('')}
    </div>
  </div>
</section>`;
}
function renderMenuList(props) {
    return `<section class="block block--menu">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    ${props.sections.map((section) => `<div class="menu-section"><h3 class="menu-section-heading">${escapeHtml(section.name)}</h3>${section.items.map((item) => `<div class="menu-item"><span class="menu-item-name">${escapeHtml(item.name)}</span><span class="menu-item-price">${escapeHtml(item.price)}</span><span class="menu-item-description">${escapeHtml(item.description)}</span></div>`).join('')}</div>`).join('')}
  </div>
</section>`;
}
function renderOpeningHours(props) {
    return `<section class="block block--hours">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <div class="hours-list">
      ${props.hours.map((hour) => `<div class="hours-row"><span class="hours-days">${escapeHtml(hour.days)}</span><span class="hours-time">${escapeHtml(hour.time)}</span></div>`).join('')}
    </div>
    ${props.note ? `<p class="block-copy">${escapeHtml(props.note)}</p>` : ''}
  </div>
</section>`;
}
function renderContact(props, slug, blockId) {
    const action = slug ? `/api/v1/public/sites/${encodeURIComponent(slug)}/contact` : `#contact-form-${safeDomId(blockId)}`;
    return `<section class="block block--contact" id="contact">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <p class="block-copy">${escapeHtml(props.body)}</p>
    <form class="contact-form" method="post" action="${escapeAttribute(action)}">
      <div class="form-field"><label for="contact-name-${safeDomId(blockId)}">Name</label><input id="contact-name-${safeDomId(blockId)}" name="name" type="text" autocomplete="name"></div>
      <div class="form-field"><label for="contact-email-${safeDomId(blockId)}">Email</label><input id="contact-email-${safeDomId(blockId)}" name="email" type="email" autocomplete="email" required></div>
      <div class="form-field"><label for="contact-phone-${safeDomId(blockId)}">Phone</label><input id="contact-phone-${safeDomId(blockId)}" name="phone" type="tel" autocomplete="tel"></div>
      <div class="form-field"><label for="contact-message-${safeDomId(blockId)}">Message</label><textarea id="contact-message-${safeDomId(blockId)}" name="message" required></textarea></div>
      <div class="hp-field" aria-hidden="true"><label for="contact-website-${safeDomId(blockId)}">Website</label><input id="contact-website-${safeDomId(blockId)}" name="website" type="text" tabindex="-1" autocomplete="off"></div>
      <button class="button button--primary" type="submit">${escapeHtml(props.buttonLabel)}</button>
    </form>
  </div>
</section>`;
}
function renderMapAddress(props) {
    return `<section class="block block--map">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    <address class="map-address">${escapeHtml(props.address)}</address>
    <a class="button button--primary" href="${escapeAttribute(safeHref(props.mapUrl))}" target="_blank" rel="noopener noreferrer">${escapeHtml(props.buttonLabel)}</a>
  </div>
</section>`;
}
function renderFaq(props) {
    return `<section class="block block--faq">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    ${props.items.map((item) => `<details class="faq-item"><summary>${escapeHtml(item.question)}</summary><p class="faq-answer">${escapeHtml(item.answer)}</p></details>`).join('')}
  </div>
</section>`;
}
function renderCta(props) {
    return `<section class="block block--cta bg-${escapeAttribute(props.background)}">
  <div class="block-inner">
    <h2 class="cta-title">${escapeHtml(props.title)}</h2>
    <p class="cta-body">${escapeHtml(props.body)}</p>
    ${renderButtonGroup(props.cta, props.secondaryCta)}
  </div>
</section>`;
}
function renderSocialLinks(props) {
    return `<section class="block block--social">
  <div class="block-inner">
    <h2 class="block-heading">${escapeHtml(props.heading)}</h2>
    ${renderLinkList(props.items, 'social-list')}
  </div>
</section>`;
}
function renderFooter(props) {
    return `<footer class="block block--footer">
  <div class="footer-inner">
    <div><div class="footer-brand">${escapeHtml(props.brand)}</div><div class="footer-tagline">${escapeHtml(props.tagline)}</div></div>
    ${renderLinkList(props.links, 'footer-links')}
    <p class="footer-copyright">${escapeHtml(props.copyright)}</p>
  </div>
</footer>`;
}
function renderSpacer(props, blockId) {
    return `<div class="block block--spacer" data-block-id="${escapeAttribute(`block-${safeDomId(blockId)}`)}" data-divider="${props.divider ? 'true' : 'false'}"><div class="spacer-line" aria-hidden="true"></div></div>`;
}
function renderBlock(block, assets, slug) {
    switch (block.type) {
        case 'header':
            return renderHeader(block.props, block.id);
        case 'hero':
            return renderHero(block.props, assets);
        case 'text':
            return renderText(block.props);
        case 'imageText':
            return renderImageText(block.props, assets);
        case 'featureGrid':
            return renderFeatureGrid(block.props);
        case 'stats':
            return renderStats(block.props);
        case 'steps':
            return renderSteps(block.props);
        case 'quote':
            return renderQuote(block.props);
        case 'banner':
            return renderBanner(block.props);
        case 'gallery':
            return renderGallery(block.props, assets);
        case 'pricing':
            return renderPricing(block.props);
        case 'testimonials':
            return renderTestimonials(block.props);
        case 'menuList':
            return renderMenuList(block.props);
        case 'openingHours':
            return renderOpeningHours(block.props);
        case 'contact':
            return renderContact(block.props, slug, block.id);
        case 'mapAddress':
            return renderMapAddress(block.props);
        case 'faq':
            return renderFaq(block.props);
        case 'cta':
            return renderCta(block.props);
        case 'socialLinks':
            return renderSocialLinks(block.props);
        case 'footer':
            return renderFooter(block.props);
        case 'spacer':
            return renderSpacer(block.props, block.id);
        case 'designed':
            return '';
    }
}
function buildSpacerCss(blocks) {
    let css = '';
    for (const block of blocks) {
        if (block.type !== 'spacer')
            continue;
        const props = block.props;
        const height = Math.max(0, Math.min(600, Math.round(props.height || 0)));
        const id = `block-${safeDomId(block.id)}`;
        css += `\n.block--spacer[data-block-id="${escapeAttribute(id)}"]{height:${height}px;min-height:${height}px}`;
    }
    return css;
}
const EDITABLE_FIELDS = {
    hero: { eyebrow: 'eyebrow', title: 'hero-title', lead: 'hero-lead' },
    text: { heading: 'block-heading' },
    cta: { title: 'cta-title', body: 'cta-body' },
    imageText: { heading: 'block-heading' },
    socialLinks: { heading: 'block-heading' },
    stats: { heading: 'block-heading' },
    steps: { heading: 'block-heading' },
    quote: { text: 'quote-text' },
    banner: { text: 'banner-text' }
};
/** Adds the section-style classes to a block's outermost element. */
function applyBlockStyle(html, style) {
    if (!style || (!style.background && !style.space))
        return html;
    const classes = [style.background ? `block-bg-${style.background}` : '', style.space ? `block-space-${style.space}` : ''].filter(Boolean).join(' ');
    return html.replace(/^(\s*<[a-zA-Z][a-zA-Z0-9]*[^>]*?\sclass=")/, `$1${classes} `);
}
function withEditorMarkers(block, html) {
    // Tag the outermost element with the real block id (separate from the renderer's own data-block-id, which spacer CSS keys on).
    let out = html.replace(/^(\s*<[a-zA-Z][a-zA-Z0-9]*)/, `$1 data-editor-block="${escapeAttribute(block.id)}"`);
    const fields = EDITABLE_FIELDS[block.type] ?? {};
    for (const [prop, cls] of Object.entries(fields)) {
        const re = new RegExp('(<[a-zA-Z][a-zA-Z0-9]*)(\\s[^>]*class="[^"]*\\b' + cls + '\\b[^"]*")');
        out = out.replace(re, `$1 data-field="${prop}"$2`);
    }
    return out;
}
// ---------------------------------------------------------------------------------------------------------------------
// Sections an owner adds to a designed template. Each one is a normal block, rendered inside a shadow root so the
// template's own CSS cannot touch it and the block CSS cannot leak into the template. Colours come from the template.
// ---------------------------------------------------------------------------------------------------------------------
function extrasVarsCss(p) {
    return `:host{display:block;--site-background:${p.background};--site-surface:${p.surface};--site-text:${p.text};--site-heading:${p.heading};--site-accent:${p.accent};--site-accentText:${p.accentText};--site-accent-base:${p.accent};--site-accentText-base:${p.accentText};--site-tint:${p.tint};--site-on-accent-muted:${p.onAccent.muted};--site-on-accent-surface:${p.onAccent.surface};--site-on-accent-border:${p.onAccent.border};--site-muted:${p.muted};--site-border:${p.border};--site-heading-font:var(--font-display,var(--font-heading,var(--font-serif,inherit)));--site-body-font:var(--font-body,var(--font-sans,inherit));--site-mono-font:ui-monospace,monospace;--site-radius-sm:.375rem;--site-radius-md:.75rem;--site-radius-lg:1.25rem;--site-section-space:3.5rem;--site-container:70rem;background:var(--site-background);color:var(--site-text);font-family:var(--site-body-font);line-height:1.6;text-align:left}`;
}
/**
 * Renders the owner-added sections of a designed page. `extraHtml` goes straight into applyDesign.
 * Anything that is not an allowed, valid block type is skipped rather than rendered.
 */
export function buildDesignExtras(props, assets = {}) {
    const extras = props.extras ?? [];
    if (extras.length === 0)
        return [];
    const entry = designManifestData.find((d) => d.id === props.designId);
    const palette = extrasPalette(entry?.vars ?? [], props.vars ?? {});
    const css = `${extrasVarsCss(palette)}\n${BASE_CSS}\n${EXTRA_BLOCK_CSS_OVERRIDES}`;
    const out = [];
    for (const extra of extras) {
        if (!DESIGN_EXTRA_TYPES.includes(extra.type) || !/^[A-Za-z0-9_-]{1,40}$/.test(extra.id))
            continue;
        const block = { id: extra.id, type: extra.type, props: extra.props, style: extra.style };
        const inner = applyBlockStyle(renderBlock(block, assets, null), block.style);
        out.push({
            id: extra.id,
            after: extra.after,
            html: `<div class="sarnia-extra" data-sarnia-extra="${escapeAttribute(extra.id)}"><template shadowrootmode="open"><style>${css}</style>${inner}</template></div>`
        });
    }
    return out;
}
// Inside the shadow root there is no <body>/<main>: keep the block layout rules that expect them harmless.
const EXTRA_BLOCK_CSS_OVERRIDES = '.block{padding-left:1.25rem;padding-right:1.25rem}';
export function renderSite(site, assets = {}, options = {}) {
    const first = site.blocks[0];
    if (site.blocks.length === 1 && first && first.type === 'designed') {
        const dp = first.props;
        const designHtml = options.designs?.[dp.designId];
        if (designHtml === undefined)
            throw new Error(`Design not available: ${dp.designId}`);
        return {
            html: applyDesign(designHtml, dp, { title: site.meta.title, description: site.meta.description, language: site.meta.language }, {
                assets: Object.fromEntries(Object.entries(assets).map(([id, a]) => [id, { url: a.url, alt: a.alt }])),
                csp: options.csp,
                siteUrl: options.siteUrl,
                extraHtml: buildDesignExtras(dp, assets),
                sectionCount: designManifestData.find((d) => d.id === dp.designId)?.sections?.length ?? 100
            }),
            css: '',
            js: ''
        };
    }
    const htmlBlocks = site.blocks
        .map((block) => {
        const html = applyBlockStyle(renderBlock(block, assets, slugFromSiteUrl(options.siteUrl)), block.style);
        return options.editorMarkers ? withEditorMarkers(block, html) : html;
    })
        .join('\n');
    const spacerCss = buildSpacerCss(site.blocks);
    const fontsHref = themeFontsHref(site.theme);
    const slug = slugFromSiteUrl(options.siteUrl);
    const abusePath = slug ? `/api/v1/public/sites/${encodeURIComponent(slug)}/abuse` : null;
    const html = `<!DOCTYPE html>
<html lang="${escapeAttribute(site.meta.language || 'en')}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="description" content="${escapeAttribute(site.meta.description)}">
<meta property="og:title" content="${escapeAttribute(site.meta.title)}">
<meta property="og:description" content="${escapeAttribute(site.meta.description)}">
<meta property="og:type" content="website">
<meta name="twitter:card" content="summary">
<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(fontsHref ? CSP_WITH_FONTS : CSP)}">
<title>${escapeHtml(site.meta.title)}</title>
${fontsHref ? `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="${escapeAttribute(fontsHref)}">
` : ''}<link rel="stylesheet" href="./site.css">
</head>
<body>
<a class="skip-link" href="#main">Skip to content</a>
<main id="main" class="site-blocks">
${htmlBlocks}
</main>
${abusePath ? `<div class="site-report-abuse"><button type="button" class="report-abuse-link" data-abuse-path="${escapeAttribute(abusePath)}">Report abuse</button></div>` : ''}
<script src="./site.js" defer></script>
</body>
</html>`;
    return {
        html,
        css: `${BASE_CSS}\n${themeToCss(site.theme)}${spacerCss}`,
        js: SITE_JS
    };
}
//# sourceMappingURL=render.js.map